/**
 * F29/F43 — EmailComposer: modal de redacción y envío de correo saliente.
 *
 * Llama a POST /emails/send. F43: adjuntos (subidos antes del envío a
 * /emails/outgoing-attachments), envío desde la cuenta del Buzón (accountId →
 * SMTP propio, From = tu dirección) y cabeceras de hilo al responder
 * (inReplyTo/references) o reenviar (forwardOfEmailId re-adjunta originales).
 * El pixel de tracking se inserta en el servidor.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Send, AlertCircle, Ban, CheckCircle2, Paperclip, X } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import type { CheckMarketingSuppressionResponse } from '@jr2os/shared'

interface Props {
  open: boolean
  onClose: () => void
  defaultTo?: string
  defaultCc?: string
  defaultSubject?: string
  defaultBody?: string
  clientId?: string
  opportunityId?: string
  /** F43 — enviar desde esta cuenta del Buzón (SMTP propio, From = su email). */
  accountId?: string
  /** F43 — hilo al responder. */
  inReplyTo?: string
  references?: string[]
  /** F43 — reenvío: el servidor re-adjunta los adjuntos del original. */
  forwardOfEmailId?: string
  onSent?: (emailSentId: string) => void
}

interface SendResponse {
  emailSentId: string
  sentAt: string
  trackingPixelId: string | null
  /** Destinatarios que están en la lista de bajas de Marketing. Ver abajo. */
  suprimidos?: string[]
}

const ATTACHMENT_MAX = 15 * 1024 * 1024

function splitEmails(s: string): string[] {
  return s
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function EmailComposer({
  open,
  onClose,
  defaultTo,
  defaultCc,
  defaultSubject,
  defaultBody,
  clientId,
  opportunityId,
  accountId,
  inReplyTo,
  references,
  forwardOfEmailId,
  onSent,
}: Props) {
  const { t } = useTranslation(['email', 'common'])
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  /**
   * Destinatarios que están en la lista de bajas de Marketing.
   *
   * AVISA, NO BLOQUEA. Esto es correo de uno a uno: una respuesta o el
   * presupuesto que alguien ha pedido no es una comunicación comercial, y
   * bloquearlo sería romper el CRM por una lectura demasiado amplia del
   * art. 21 LSSI-CE. Quien manda sabe lo que está haciendo; lo que no puede
   * es no saber que esa persona pidió no recibir nada. Las campañas, que sí
   * son publicidad, no saldrán nunca hacia esta gente.
   */
  const [suprimidos, setSuprimidos] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset SOLO en la transición cerrado→abierto: si un default cambia con el
  // modal ya abierto (p. ej. /content resuelve tarde y recalcula la cita), no
  // se debe pisar lo que el usuario ya ha escrito o adjuntado.
  const prevOpen = useRef(false)
  useEffect(() => {
    if (open && !prevOpen.current) {
      setTo(defaultTo ?? '')
      setCc(defaultCc ?? '')
      setBcc('')
      setSubject(defaultSubject ?? '')
      setBody(defaultBody ?? '')
      setFiles([])
      setError(null)
      setSuccess(false)
      setSuprimidos([])
    }
    prevOpen.current = open
  }, [open, defaultTo, defaultCc, defaultSubject, defaultBody])

  // Comprobación de bajas mientras se escriben los destinatarios. Con retardo
  // para no preguntar en cada tecla, y sin molestar si falla: un aviso que no
  // se puede dar no debe impedir escribir un correo.
  useEffect(() => {
    if (!open) return
    const destinatarios = [...splitEmails(to), ...splitEmails(cc), ...splitEmails(bcc)].filter(
      (e) => e.includes('@'),
    )
    if (destinatarios.length === 0) {
      setSuprimidos([])
      return
    }
    let vigente = true
    const id = window.setTimeout(() => {
      api
        .post<CheckMarketingSuppressionResponse>('/marketing/suppressions/check', {
          emails: destinatarios.slice(0, 500),
        })
        .then((r) => {
          if (vigente) setSuprimidos(r.suprimidos.map((s) => s.email))
        })
        .catch(() => {
          if (vigente) setSuprimidos([])
        })
    }, 500)
    return () => {
      vigente = false
      window.clearTimeout(id)
    }
  }, [open, to, cc, bcc])

  const addFiles = (list: FileList | null) => {
    if (!list) return
    const next = [...files]
    for (const f of Array.from(list)) {
      if (f.size > ATTACHMENT_MAX) {
        setError(t('composer.attachmentTooBig', { name: f.name }))
        continue
      }
      next.push(f)
    }
    setFiles(next.slice(0, 20))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    const toList = splitEmails(to)
    if (toList.length === 0) {
      setError(t('errors.recipientRequired'))
      return
    }
    if (!subject.trim()) {
      setError(t('errors.subjectRequired'))
      return
    }
    if (!body.trim()) {
      setError(t('errors.bodyRequired'))
      return
    }

    // El usuario escribe en texto plano: lo envolvemos en HTML mínimo para
    // que el pixel de tracking quede dentro del documento. El servidor reusa
    // ese HTML tal cual y solo le anexa el <img> del pixel.
    const escaped = body
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>')
    const bodyHtml = `<div style="font-family:sans-serif;font-size:14px;line-height:1.5;color:#0f172a">${escaped}</div>`

    setSubmitting(true)
    try {
      // F43 — sube los adjuntos primero; el envío referencia sus keys.
      let attachmentKeys: string[] | undefined
      if (files.length > 0) {
        const form = new FormData()
        for (const f of files) form.append('files', f, f.name)
        const up = await api.upload<{ attachments: { key: string }[] }>(
          '/emails/outgoing-attachments',
          form,
        )
        attachmentKeys = up.attachments.map((a) => a.key)
      }

      const res = await api.post<SendResponse>('/emails/send', {
        to: toList,
        cc: splitEmails(cc),
        bcc: splitEmails(bcc),
        subject: subject.trim(),
        bodyHtml,
        bodyText: body,
        clientId,
        opportunityId,
        attachmentKeys,
        accountId,
        inReplyTo,
        references: references && references.length ? references : undefined,
        forwardOfEmailId,
      })
      setSuccess(true)
      // El servidor tiene la última palabra sobre las bajas: el aviso de
      // arriba lleva medio segundo de retardo, y una dirección pegada justo
      // antes de enviar puede no haber pasado por él.
      if (res.suprimidos?.length) setSuprimidos(res.suprimidos)
      if (onSent) onSent(res.emailSentId)
      // Cerramos tras un momento para que el usuario vea la confirmación. Si
      // ha ido a alguien dado de baja, se le deja tiempo para leerlo.
      window.setTimeout(
        () => {
          onClose()
        },
        res.suprimidos?.length ? 4000 : 700,
      )
    } catch (err) {
      if (err instanceof ApiError) {
        const errBody = err.body as { error?: string; code?: string } | undefined
        setError(errBody?.error ?? `${t('errors.errorPrefix')} ${err.status}`)
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal open={open} onClose={onClose} title={t('composer.title')} maxWidth="max-w-2xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('composer.to')}</label>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={t('composer.toPlaceholder')}
            className={inputCls}
            autoFocus
            required
          />
          <p className="text-[11px] t-faint mt-1">
            {t('composer.recipientsHint')}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('composer.cc')}</label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder={t('composer.optionalPlaceholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('composer.bcc')}</label>
            <input
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder={t('composer.optionalPlaceholder')}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('composer.subject')}</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('composer.message')}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className={`${inputCls} font-sans`}
            required
          />
          <p className="text-[11px] t-faint mt-1">
            {t('composer.trackingHint')}
          </p>
        </div>

        {/* F43 — Adjuntos */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            <Paperclip size={13} /> {t('composer.attach')}
          </button>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="surface-soft px-3 py-1.5 text-xs flex items-center gap-2"
                >
                  <Paperclip size={12} className="text-jr2-700 dark:text-jr2-300 shrink-0" />
                  <span className="t-primary truncate flex-1 min-w-0">{f.name}</span>
                  <span className="t-faint shrink-0">{formatSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    className="t-faint hover:text-rose-600 dark:hover:text-rose-300 shrink-0"
                    aria-label={t('common:actions.delete')}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {suprimidos.length > 0 && (
          <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 flex items-start gap-2">
            <Ban size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong className="font-semibold">
                {t('composer.suppressed.title', { list: suprimidos.join(', ') })}
              </strong>{' '}
              {t('composer.suppressed.body')}
            </span>
          </div>
        )}

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-3 py-2 flex items-center gap-2">
            <CheckCircle2 size={14} /> {t('composer.sentSuccess')}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            {t('composer.send')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
